import { PROBLEM_LINK, aiHealthBannerText } from "@/features/conversations/ai-status";
import { api, readableApiError } from "@/shared/api";
import { formatDate, formatTime } from "@/shared/format";
import { queryKeys } from "@/shared/query";
import { cx } from "@/shared/ui/primitives";
import { useToast } from "@/shared/ui/toast";
import type { AiFailedChatDto } from "@repo/polaris-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CircleAlert } from "lucide-react";
import { useId, useState } from "react";

function sincePhrase(value: string) {
  return new Date(value).toDateString() === new Date().toDateString()
    ? `dalle ${formatTime(value)}`
    : `dal ${formatDate(value)}`;
}

/** "alle 19:43" today, the day and time before. */
function whenPhrase(value: string) {
  return new Date(value).toDateString() === new Date().toDateString()
    ? `alle ${formatTime(value)}`
    : formatDate(value);
}

/**
 * Owners and admins see when the AI stops for everyone (no OpenAI credit, a
 * refused key) and the chats it left without a proposal (ADR 0072): the one
 * chat by name, more in a list, each to open, ask again or let go.
 */
export function AiHealthBanner({ workspaceSlug }: { workspaceSlug: string }) {
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const listId = useId();
  const [listOpen, setListOpen] = useState(false);
  const health = useQuery({
    queryKey: queryKeys.aiHealth(workspaceSlug),
    queryFn: () => api.ai.health(workspaceSlug),
    staleTime: 30_000,
    // Events say when it changes; this covers a missed one or an expired problem.
    refetchInterval: 5 * 60_000,
  });
  const refresh = (leadId?: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.aiHealth(workspaceSlug) }),
      leadId
        ? queryClient.invalidateQueries({ queryKey: queryKeys.lead(workspaceSlug, leadId) })
        : null,
    ]);
  const failedToast = (title: string) => (error: unknown) =>
    pushToast({
      tone: "error",
      title,
      description: readableApiError(error, "Riprova tra qualche secondo."),
    });
  const retryAll = useMutation({
    mutationFn: () => api.ai.retryFailed(workspaceSlug),
    onSuccess: async (result) => {
      await refresh();
      pushToast({
        tone: "info",
        title:
          result.queued === 1
            ? "Proposta AI richiesta per 1 chat"
            : `Proposte AI richieste per ${result.queued} chat`,
        description: "Compariranno nelle conversazioni e richiederanno la tua approvazione.",
      });
    },
    onError: failedToast("Richiesta all'AI non partita"),
  });
  const retryOne = useMutation({
    mutationFn: (chat: AiFailedChatDto) =>
      api.leads.generateProposal(workspaceSlug, chat.leadId, chat.retry),
    onSuccess: async (_result, chat) => {
      await refresh(chat.leadId);
      pushToast({
        tone: "info",
        title: `Proposta AI richiesta per ${chat.displayName}`,
        description: "Comparirà nella conversazione e richiederà la tua approvazione.",
      });
    },
    onError: failedToast("Richiesta all'AI non partita"),
  });
  const dismiss = useMutation({
    mutationFn: (chat: AiFailedChatDto) =>
      api.leads.dismissFailedProposal(workspaceSlug, chat.leadId),
    onSuccess: (_result, chat) => refresh(chat.leadId),
    onError: failedToast("Errore non ignorato"),
  });
  const banner = health.data ? aiHealthBannerText(health.data, sincePhrase) : null;
  if (!banner) return null;
  const link = health.data?.problem ? PROBLEM_LINK[health.data.problem.kind] : undefined;
  const busy = retryAll.isPending || retryOne.isPending || dismiss.isPending;
  const chatActions = (chat: AiFailedChatDto) => (
    <>
      <Link
        to="/w/$workspaceSlug/inbox/$leadId"
        params={{ workspaceSlug, leadId: chat.leadId }}
        className="text-button"
        onClick={() => setListOpen(false)}
      >
        Apri
      </Link>
      <button
        type="button"
        className="text-button"
        disabled={busy}
        onClick={() => retryOne.mutate(chat)}
      >
        Riprova
      </button>
      <button
        type="button"
        className="text-button"
        title="Va bene così: l'avviso sparisce, l'AI riparte al prossimo messaggio del lead"
        disabled={busy}
        onClick={() => dismiss.mutate(chat)}
      >
        Ignora
      </button>
    </>
  );
  const chats = health.data?.failedChats ?? [];
  return (
    <div className="ai-health">
      <output className={cx("ai-health-banner", `ai-health-banner--${banner.tone}`)}>
        <CircleAlert aria-hidden="true" size={16} />
        <span>
          {banner.chat ? (
            <>
              La chat con{" "}
              <Link
                to="/w/$workspaceSlug/inbox/$leadId"
                params={{ workspaceSlug, leadId: banner.chat.leadId }}
                className="ai-health-banner__lead"
              >
                {banner.chat.displayName}
              </Link>{" "}
            </>
          ) : null}
          {banner.text}
          {banner.hint ? <span className="ai-health-banner__hint"> {banner.hint}</span> : null}
        </span>
        {link ? (
          <a className="text-button" href={link.href} target="_blank" rel="noopener noreferrer">
            {link.label}
          </a>
        ) : null}
        {banner.chat ? chatActions(banner.chat) : null}
        {banner.listLabel ? (
          <button
            type="button"
            className="text-button"
            aria-expanded={listOpen}
            aria-controls={listId}
            onClick={() => setListOpen(!listOpen)}
          >
            {listOpen ? "Chiudi l'elenco" : banner.listLabel}
          </button>
        ) : null}
        {banner.retryLabel && !banner.chat ? (
          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => retryAll.mutate()}
          >
            {banner.retryLabel}
          </button>
        ) : null}
      </output>
      {listOpen && banner.listLabel ? (
        <ul
          className={cx("ai-failed-list", `ai-failed-list--${banner.tone}`)}
          id={listId}
          aria-label="Chat rimaste senza proposta AI"
        >
          {chats.map((chat) => (
            <li key={chat.leadId}>
              <span className="ai-failed-list__lead">
                <strong>{chat.displayName}</strong>
                <small>
                  {whenPhrase(chat.failedAt)}
                  {chat.reason ? ` · ${chat.reason}` : ""}
                </small>
              </span>
              <span className="ai-failed-list__actions">{chatActions(chat)}</span>
            </li>
          ))}
          {health.data && health.data.failedLeads > chats.length ? (
            <li className="ai-failed-list__more">
              E altre {health.data.failedLeads - chats.length}, più vecchie.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
